import type { GatshipSearchOperator } from '@gat-ship/types'
import { getSearchOperatorValue } from '../components/search/stakeholder-search-operators'

/**
 * Plain-data snapshot of a single search filter. The reactive
 * `useStakeholderSearchFilters()` refs are read into values by the shell before
 * any pure decision is taken over them, keeping this core free of `vue`.
 */
export interface StakeholderFilterField {
  fieldName: string
  operator: GatshipSearchOperator
  filterValue: string | undefined
}

/**
 * Plain-data snapshot of the full stakeholder filter set, keyed exactly like
 * `useStakeholderSearchFilters()`. Insertion order is preserved by the shell so
 * the generated query string is byte-identical to the legacy implementation.
 */
export interface StakeholderFilterSnapshot {
  type: StakeholderFilterField
  name: StakeholderFilterField
  email: StakeholderFilterField
  phone: StakeholderFilterField
  state: StakeholderFilterField
  country: StakeholderFilterField
  vendorNumber: StakeholderFilterField
  clientNumber: StakeholderFilterField
}

/**
 * Build a single `field:<operator><value>` query token. Returns '' for an empty
 * value so the caller can drop it from the joined query.
 */
function constructQuery(
  fieldName: string,
  operator: GatshipSearchOperator,
  fieldValue: string | undefined,
  options?: {
    quotedValue?: boolean
  }
): string {
  if (!fieldValue) {
    return ''
  }
  const quotedValue = options?.quotedValue ?? true
  const value = quotedValue ? `"${fieldValue}"` : fieldValue
  return `${fieldName}:${getSearchOperatorValue(operator)}${value}`
}

/**
 * Build the stakeholder search query string from a filter snapshot. The
 * `client`/`vendor` types special-case their number field; every other filter
 * is joined generically with the `state` field expanded when set to `any`.
 * Output is byte-identical to the legacy shell builder — both quick and
 * advanced search share it.
 */
export function buildFilterQuery(filters: StakeholderFilterSnapshot): string {
  const typeField = constructQuery(
    filters.type.fieldName,
    filters.type.operator,
    filters.type.filterValue
  )
  const statusField = constructQuery(
    filters.state.fieldName,
    filters.state.operator,
    filters.state.filterValue
  )
  if (
    filters.type.filterValue === 'client' &&
    filters.clientNumber.filterValue
  ) {
    const clientNumberField = constructQuery(
      filters.clientNumber.fieldName,
      filters.clientNumber.operator,
      filters.clientNumber.filterValue
    )
    return `${clientNumberField} ${typeField} ${statusField}`
  }
  if (
    filters.type.filterValue === 'vendor' &&
    filters.vendorNumber.filterValue
  ) {
    const vendorNumberField = constructQuery(
      filters.vendorNumber.fieldName,
      filters.vendorNumber.operator,
      filters.vendorNumber.filterValue
    )
    return `${vendorNumberField} ${typeField} ${statusField}`
  }
  const query: string[] = []
  Object.entries(filters)
    .filter(([key]) => key !== 'state')
    .forEach(([key, value]) => {
      query.push(constructQuery(key, value.operator, value.filterValue))
    })

  const stateField = constructQuery(
    filters.state.fieldName,
    filters.state.operator,
    filters.state.filterValue === 'any'
      ? '("active" or "inactive")'
      : filters.state.filterValue,
    {
      quotedValue: filters.state.filterValue !== 'any'
    }
  )
  query.push(stateField)
  return query.filter(Boolean).join(' ')
}

/**
 * Project the active filters for the current search. The `client`/`vendor`
 * types special-case their number field (returning type, state and that number
 * field only); otherwise every filter holding a value is included. Mirrors
 * {@link buildFilterQuery}'s precedence so the displayed search matches the
 * query that ran.
 */
export function selectActiveFilters(
  filters: StakeholderFilterSnapshot
): Record<string, StakeholderFilterField> {
  const activeFilters: Record<string, StakeholderFilterField> = {}
  const stateField: StakeholderFilterField = {
    filterValue: filters.state.filterValue,
    operator: filters.state.operator,
    fieldName: filters.state.fieldName
  }
  const typeField: StakeholderFilterField = {
    filterValue: filters.type.filterValue,
    operator: filters.type.operator,
    fieldName: filters.type.fieldName
  }
  if (
    filters.type.filterValue === 'client' &&
    filters.clientNumber.filterValue
  ) {
    activeFilters.type = typeField
    activeFilters.state = stateField
    activeFilters.clientNumber = {
      filterValue: filters.clientNumber.filterValue,
      operator: filters.clientNumber.operator,
      fieldName: filters.clientNumber.fieldName
    }
    return activeFilters
  }
  if (
    filters.type.filterValue === 'vendor' &&
    filters.vendorNumber.filterValue
  ) {
    activeFilters.type = typeField
    activeFilters.state = stateField
    activeFilters.vendorNumber = {
      filterValue: filters.vendorNumber.filterValue,
      operator: filters.vendorNumber.operator,
      fieldName: filters.vendorNumber.fieldName
    }
    return activeFilters
  }
  Object.entries(filters)
    .filter(([, value]) => value.filterValue)
    .forEach(([key, value]) => {
      activeFilters[key] = {
        filterValue: value.filterValue,
        operator: value.operator,
        fieldName: value.fieldName
      }
    })
  return activeFilters
}

/**
 * Whether any non-default search filter is set. The `state` and `type` filters
 * always carry a default value, so they are excluded; the remaining filters are
 * dirty when any holds a non-empty value.
 */
export function areFiltersDirty(filters: StakeholderFilterSnapshot): boolean {
  const excluded = ['state', 'type']
  return Object.values(filters)
    .filter((filter) => !excluded.includes(filter.fieldName))
    .some(
      (filter) => filter.filterValue !== undefined && filter.filterValue !== ''
    )
}
